import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { FormattedMessage, injectIntl } from "react-intl";
import * as auth from "../_redux/authRedux";
import { login } from "../_redux/authCrud";

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

const initialValues = {
  userName: "",
  password: "",
};
/*
const initialValues = {
  userName: "admin",
  password: "Test123!",
};
*/

function Login(props) {
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const LoginSchema = Yup.object().shape({
    userName: Yup.string()
      .min(
        1,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MIN_LENGTH",
            defaultMessage: "AUTH.VALIDATION.MIN_LENGTH",
          },
          { name: intl.formatMessage({
              id: "AUTH.INPUT.USERNAME",
              defaultMessage: "AUTH.INPUT.USERNAME",
            }), 
            min: "1" }
        )
      )
      .max(
        30,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MAX_LENGTH",
            defaultMessage: "AUTH.VALIDATION.MAX_LENGTH",
          },
          { name: intl.formatMessage({
              id: "AUTH.INPUT.USERNAME",
              defaultMessage: "AUTH.INPUT.USERNAME",
            }),
            max: "30" }
        )
      )
      .required(
        intl.formatMessage({
          id: "VALIDATION.REQUIRED_FIELD",
          defaultMessage: "VALIDATION.REQUIRED_FIELD",
        })
      ),
    password: Yup.string()
      .max(
        50,
        intl.formatMessage(
          {
            id: "AUTH.VALIDATION.MAX_LENGTH",
            defaultMessage: "AUTH.VALIDATION.MAX_LENGTH",
          },
          {
            name: intl.formatMessage({
              id: "AUTH.INPUT.PASSWORD",
              defaultMessage: "AUTH.INPUT.PASSWORD",
            }),
            max: "50",
          }
        )
      )
      .required(
        intl.formatMessage({
          id: "VALIDATION.REQUIRED_FIELD",
          defaultMessage: "VALIDATION.REQUIRED_FIELD",
        })
      ),
  });

  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      enableLoading();

      login(values.userName, values.password)
        .then(({ data }) => {
          disableLoading();
          if (data.authenticated) props.login(data.token);
          else setStatus(data.message);
        })
        .catch(() => {
          setStatus(
            intl.formatMessage({
              id: "AUTH.VALIDATION.INVALID_LOGIN",
              defaultMessage: "AUTH.VALIDATION.INVALID_LOGIN",
            })
          );
        })
        .finally(() => {
          disableLoading();
          setSubmitting(false);
        });
    },
  });

  return (
    <div className="login-form login-signin" id="kt_login_signin_form">
      {/* begin::Head */}
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          <FormattedMessage
            id="AUTH.LOGIN.TITLE"
            defaultMessage="AUTH.LOGIN.TITLE"
          />
        </h3>
        <p className="text-muted font-weight-bold">
          <FormattedMessage
            id="AUTH.LOGIN.SUBTITLE"
            defaultMessage="AUTH.LOGIN.SUBTITLE"
          />
        </p>
      </div>
      {/* end::Head */}

      {/*begin::Form*/}
      <form
        onSubmit={formik.handleSubmit}
        className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {formik.status && (
          <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        )}

        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder={intl.formatMessage({
              id: "AUTH.INPUT.USERNAME",
              defaultMessage: "AUTH.INPUT.USERNAME",
            })}
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              "userName"
            )}`}
            name="userName"
            {...formik.getFieldProps("userName")}
          />
          {formik.touched.userName && formik.errors.userName ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.userName}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group fv-plugins-icon-container">
          <input
            placeholder={intl.formatMessage({
              id: "AUTH.INPUT.PASSWORD",
              defaultMessage: "AUTH.INPUT.PASSWORD",
            })}
            type="password"
            className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
              "password"
            )}`}
            name="password"
            {...formik.getFieldProps("password")}
          />
          {formik.touched.password && formik.errors.password ? (
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">{formik.errors.password}</div>
            </div>
          ) : null}
        </div>
        <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
          <Link
            to="/auth/forgot-password"
            className="text-dark-50 text-hover-primary my-3 mr-2"
            id="kt_login_forgot"
          >
            <FormattedMessage
              id="AUTH.GENERAL.FORGOT_BUTTON"
              defaultMessage="AUTH.GENERAL.FORGOT_BUTTON"
            />
          </Link>
          <button
            id="kt_login_signin_submit"
            type="submit"
            disabled={formik.isSubmitting}
            className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
          >
            <span>
              <FormattedMessage
                id="AUTH.LOGIN.BUTTON"
                defaultMessage="AUTH.LOGIN.BUTTON"
              />
            </span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>
        </div>
      </form>
      {/*end::Form*/}
    </div>
  );
}

export default injectIntl(connect(null, auth.actions)(Login));
